import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Loader2, Save, ArrowUpLeft, ArrowUpRight, ArrowDown } from 'lucide-react';
import { AppleWalletPreview } from './AppleWalletPreview';

const FieldMapItem = ({ position, label, color }) => (
  <div className="flex items-center gap-2">
    <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${color}`}>
      {position === 'top-left' && <ArrowUpLeft className="w-3 h-3 text-white" />}
      {position === 'top-right' && <ArrowUpRight className="w-3 h-3 text-white" />}
      {position === 'bottom' && <ArrowDown className="w-3 h-3 text-white" />}
    </div>
    <span className="text-xs text-slate-600">{label}</span>
  </div>
);

export const DesignTab = ({ formData, handleChange, handleSubmit, saving, isNew, getLogoUrl }) => (
  <div className="grid lg:grid-cols-[1fr,480px] gap-8">
    <div className="space-y-6">
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle>Identité de la carte</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nom de la carte *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="ex: Carte Fidélité Café"
                required
                data-testid="card-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description || ''}
                onChange={handleChange}
                placeholder="ex: Cumulez des points à chaque achat"
                data-testid="card-description-input"
              />
            </div>
            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={saving} data-testid="save-card-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {isNew ? 'Créer la carte' : 'Enregistrer'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Point 1: Aperçu des informations */}
      <Card className="border-slate-200 bg-slate-50/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Placement des champs sur le pass</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3">
            <FieldMapItem position="top-left" label="Logo + Nom de la carte (en-tête gauche)" color="bg-blue-500" />
            <FieldMapItem position="top-right" label="Compteur points/tampons (en-tête droite)" color="bg-purple-500" />
            <FieldMapItem position="bottom" label="Nom du membre (champ secondaire)" color="bg-green-500" />
            <FieldMapItem position="bottom" label="Récompense (champ secondaire droite)" color="bg-amber-500" />
          </div>
          <div className="mt-4 rounded-xl overflow-hidden border border-slate-200 bg-white p-3">
            <div className="relative text-[11px]">
              {/* Mini pass schéma */}
              <div className="rounded-lg p-2.5" style={{ backgroundColor: formData.primary_color || '#2563EB', color: formData.secondary_color || '#FFF' }}>
                <div className="flex justify-between items-start mb-1">
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 rounded bg-white/20" />
                    <span className="font-semibold truncate max-w-[120px]" style={{ fontSize: '10px' }}>{formData.name || '---'}</span>
                    <span className="text-[8px] ml-1 px-1 py-0.5 rounded bg-blue-500/30 text-white">Nom</span>
                  </div>
                  <div className="text-right">
                    <span style={{ fontSize: '9px' }} className="opacity-60">POINTS</span>
                    <span className="text-[8px] ml-1 px-1 py-0.5 rounded bg-purple-500/30 text-white">Compteur</span>
                  </div>
                </div>
                <div className="h-8 rounded bg-white/10 mb-1 flex items-center justify-center">
                  <span style={{ fontSize: '8px' }} className="opacity-40">Zone visuelle (onglet Visuel)</span>
                </div>
                <div className="flex justify-between" style={{ fontSize: '9px' }}>
                  <div>
                    <span className="opacity-50">MEMBRE</span>
                    <span className="text-[7px] ml-1 px-1 py-0.5 rounded bg-green-500/30 text-white">Auto</span>
                  </div>
                  <div>
                    <span className="opacity-50">RÉCOMPENSE</span>
                    <span className="text-[7px] ml-1 px-1 py-0.5 rounded bg-amber-500/30 text-white">Fidélité</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <p className="text-[11px] text-slate-400 mt-2 text-center">Les couleurs se configurent dans l'onglet Couleurs, les récompenses dans Fidélité</p>
        </CardContent>
      </Card>
    </div>

    {/* Preview — sticky + 20% bigger (point 5) */}
    <div className="lg:sticky lg:top-5 self-start">
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle>Aperçu Apple Wallet</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="transform scale-[1.05] origin-top">
            <AppleWalletPreview formData={formData} getLogoUrl={getLogoUrl} variant="full" />
          </div>
        </CardContent>
      </Card>
    </div>
  </div>
);
